import React, { useState } from "react"
import { Avatar, Modal } from "antd"
import {
	RobotOutlined,
	CheckOutlined,
	CheckCircleOutlined,
} from "@ant-design/icons"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Message, Group, GroupMember } from "firebase/groupsService"
import {
	formatTime,
	formatDate,
	isSameDay,
	getInitials,
	renderTextWithMentions,
} from "../utils"
import { ImageAttachment } from "./ImageAttachment"
import { FileAttachment } from "./FileAttachment"
import strings from "utils/strings"

interface MessageListProps {
	messages: Message[]
	loggedUserId: string
	selectedGroup: Group
}

function renderDateSeparator(date: Date) {
	const label = formatDate(date)
	return (
		<div
			key={`sep-${date.toISOString()}`}
			className="msg-date-separator"
			data-date={label}
		>
			<span>{label}</span>
		</div>
	)
}

function renderBubbleContent(
	msg: Message,
	bubbleClass: string,
	members: Group["members"]
) {
	const isImage = msg.attachmentType === "image"
	const isFile = msg.attachmentType === "file"
	const hasText = !isFile && msg.content.trim().length > 0
	const variant = bubbleClass.replace("msg-bubble--", "") as
		| "me"
		| "other"
		| "bot"

	return (
		<div className={`msg-bubble ${bubbleClass}`}>
			{isImage && <ImageAttachment url={msg.attachmentUrl!} />}
			{isFile && (
				<FileAttachment
					url={msg.attachmentUrl!}
					filename={msg.content || "ficheiro"}
				/>
			)}
			{hasText &&
				(variant === "bot" ? (
					<div className="msg-text msg-text--markdown">
						<ReactMarkdown remarkPlugins={[remarkGfm]}>
							{msg.content}
						</ReactMarkdown>
					</div>
				) : (
					<span className="msg-text">
						{renderTextWithMentions(msg.content, variant, members)}
					</span>
				))}
			<span className="msg-time">{formatTime(msg.createdAt)}</span>
		</div>
	)
}

// ── Read receipts ─────────────────────────────────────────────────────────────

function getReadBy(
	msg: Message,
	members: GroupMember[],
	lastReadAt: Record<string, Date>
): GroupMember[] {
	return members
		.filter((m) => m.id !== msg.senderId && m.type !== "bot")
		.filter((m) => {
			const t = lastReadAt[m.id]
			return t && t.getTime() >= msg.createdAt.getTime()
		})
}

function ReadReceipts({
	msg,
	members,
	lastReadAt,
}: {
	msg: Message
	members: GroupMember[]
	lastReadAt: Record<string, Date>
}) {
	const [open, setOpen] = useState(false)
	const readBy = getReadBy(msg, members, lastReadAt)
	const total = members.filter(
		(m) => m.id !== msg.senderId && m.type !== "bot"
	).length
	const notRead = members
		.filter((m) => m.id !== msg.senderId && m.type !== "bot")
		.filter((m) => !readBy.find((r) => r.id === m.id))

	if (total === 0) return null

	const allRead = readBy.length >= total
	const MAX_VISIBLE = 3

	return (
		<>
			<div
				className={`read-receipts${readBy.length > 0 ? " read-receipts--clickable" : ""}`}
				onClick={() => readBy.length > 0 && setOpen(true)}
				title={
					readBy.length > 0
						? strings.messaging.seeWhoRead
						: strings.messaging.nobodyRead
				}
			>
				{readBy.length === 0 ? (
					// Single grey tick
					<CheckOutlined className="receipt-tick receipt-tick--pending" />
				) : (
					<>
						{readBy.slice(0, MAX_VISIBLE).map((m) => (
							<Avatar
								key={m.id}
								size={16}
								style={{
									background: allRead ? "#2B6CF4" : "#64748B",
									fontSize: 8,
									fontWeight: 700,
									marginLeft: -4,
									border: "1.5px solid #fff",
								}}
							>
								{getInitials(m.name)}
							</Avatar>
						))}
						{readBy.length > MAX_VISIBLE && (
							<span className="receipt-overflow">
								+{readBy.length - MAX_VISIBLE}
							</span>
						)}
						<CheckCircleOutlined
							className={`receipt-tick ${allRead ? "receipt-tick--all" : "receipt-tick--some"}`}
						/>
					</>
				)}
			</div>

			<Modal
				title={
					<span
						style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14 }}
					>
						{strings.messaging.readReceiptsTitle}
					</span>
				}
				visible={open}
				onCancel={() => setOpen(false)}
				footer={null}
				width={340}
				centered
				className="create-group-modal"
				bodyStyle={{ padding: "12px 20px 20px" }}
			>
				{readBy.length > 0 && (
					<div className="receipt-modal-section">
						<div className="receipt-modal-label receipt-modal-label--read">
							<CheckCircleOutlined /> {strings.messaging.seenBy} {readBy.length}
						</div>
						{readBy.map((m) => (
							<div key={m.id} className="receipt-modal-row">
								<Avatar
									size={28}
									style={{ background: "#2B6CF4", fontSize: 11 }}
								>
									{getInitials(m.name)}
								</Avatar>
								<span className="receipt-modal-name">{m.name}</span>
								{lastReadAt[m.id] && (
									<span className="receipt-modal-time">
										{formatTime(lastReadAt[m.id])}
									</span>
								)}
							</div>
						))}
					</div>
				)}
				{notRead.length > 0 && (
					<div className="receipt-modal-section">
						<div className="receipt-modal-label receipt-modal-label--pending">
							<CheckOutlined /> {strings.messaging.unread} ({notRead.length})
						</div>
						{notRead.map((m) => (
							<div key={m.id} className="receipt-modal-row">
								<Avatar
									size={28}
									style={{ background: "#94A3B8", fontSize: 11 }}
								>
									{getInitials(m.name)}
								</Avatar>
								<span className="receipt-modal-name">{m.name}</span>
							</div>
						))}
					</div>
				)}
			</Modal>
		</>
	)
}

// ── Main render ───────────────────────────────────────────────────────────────

function renderMessage(
	msg: Message,
	showAvatar: boolean,
	loggedUserId: string,
	members: Group["members"],
	lastReadAt: Record<string, Date>
) {
	const isMe = msg.senderId === loggedUserId
	const isBot = msg.senderType === "bot"
	const newSenderClass = showAvatar ? " msg-row--new-sender" : ""

	if (isBot) {
		return (
			<div key={msg.id} className={`msg-row msg-row--bot${newSenderClass}`}>
				<div className="msg-avatar msg-avatar--bot">
					<RobotOutlined />
				</div>
				<div className="msg-content">
					<div className="msg-sender-name msg-sender-name--bot">
						<span>Dingoo Bot</span>
						<span className="bot-badge">agent d</span>
					</div>
					{renderBubbleContent(msg, "msg-bubble--bot", members)}
				</div>
			</div>
		)
	}

	if (isMe) {
		return (
			<div key={msg.id} className={`msg-row msg-row--me${newSenderClass}`}>
				<div className="msg-content msg-content--me">
					{renderBubbleContent(msg, "msg-bubble--me", members)}
					<ReadReceipts msg={msg} members={members} lastReadAt={lastReadAt} />
				</div>
			</div>
		)
	}

	return (
		<div key={msg.id} className={`msg-row msg-row--other${newSenderClass}`}>
			<div className="msg-avatar">
				{showAvatar ? (
					<Avatar size={28} style={{ background: "#2C3E50", fontSize: 11 }}>
						{getInitials(msg.senderName)}
					</Avatar>
				) : (
					<div style={{ width: 28 }} />
				)}
			</div>
			<div className="msg-content">
				{showAvatar && <div className="msg-sender-name">{msg.senderName}</div>}
				{renderBubbleContent(msg, "msg-bubble--other", members)}
			</div>
		</div>
	)
}

export const MessageList: React.FC<MessageListProps> = ({
	messages,
	loggedUserId,
	selectedGroup,
}) => {
	const items: React.ReactNode[] = []
	let lastDate: Date | null = null
	let lastSenderId: string | null = null
	const { members, lastReadAt } = selectedGroup

	for (const msg of messages) {
		const msgDate = new Date(
			msg.createdAt.getFullYear(),
			msg.createdAt.getMonth(),
			msg.createdAt.getDate()
		)
		if (!lastDate || !isSameDay(lastDate, msgDate)) {
			items.push(renderDateSeparator(msgDate))
			lastDate = msgDate
		}
		const showAvatar = msg.senderId !== lastSenderId
		items.push(
			renderMessage(msg, showAvatar, loggedUserId, members, lastReadAt)
		)
		lastSenderId = msg.senderId
	}

	return <>{items}</>
}
